import * as React from 'react'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { DiscountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { dollarsToCents } from '@/lib/currency-utils'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'

interface UseFinancialDocumentTotalsOptions {
  items: QuoteLineItem[]
  discountType?: DiscountTypeEnum
  discountValue?: number
  enforcedTipPercentage?: number
  manualTipCents?: number // Invoice-only: manual tip amount in cents
  existingTotals?: {
    subtotalCents: number
    discountCents: number
    salesTaxCents: number
    enforcedTipCents?: number
    totalCents: number
  }
  isReadOnly?: boolean
}

interface FinancialDocumentTotals {
  subtotalCents: number
  discountAmountCents: number
  discountRatio: number
  taxAmountCents: number
  enforcedTipCents: number
  tipCents: number // For invoices: max(enforced, manual); for quotes: enforced only
  totalCents: number // Tip-exclusive: subtotal + tax - discount
  grandTotalCents: number // Tip-inclusive: totalCents + tipCents
}

/**
 * useFinancialDocumentTotals - Centralized totals calculation for invoices and quotes
 *
 * This hook provides a single source of truth for all financial calculations,
 * ensuring consistency across invoice and quote components.
 *
 * Calculation order:
 * 1. Subtotal (sum of line items)
 * 2. Discount (percentage or fixed amount)
 * 3. Discount ratio (for proportional tax calculation)
 * 4. Tax (per-line-item with discount ratio applied)
 * 5. Enforced tip (percentage of subtotal after discount)
 * 6. Manual tip (invoice-only, in cents)
 * 7. Tip to use (max of enforced and manual for invoices)
 * 8. Total (subtotal + tax - discount) — tip-exclusive
 * 9. Grand total (total + tip) — tip-inclusive
 *
 * @param options - Configuration options
 * @returns Calculated totals in cents
 */
export function useFinancialDocumentTotals({
  items,
  discountType,
  discountValue,
  enforcedTipPercentage,
  manualTipCents = 0,
  existingTotals,
  isReadOnly = false,
}: UseFinancialDocumentTotalsOptions): FinancialDocumentTotals {
  // 1. Subtotal
  const subtotalCents = React.useMemo(() => {
    if (isReadOnly && existingTotals) {
      return existingTotals.subtotalCents
    }
    return items.reduce(
      (sum, item) => sum + calculateLineTotalCents(item.quantity, item.unitPriceCents),
      0
    )
  }, [isReadOnly, existingTotals, items])

  // 2. Discount
  const discountAmountCents = React.useMemo(() => {
    if (isReadOnly && existingTotals) {
      return existingTotals.discountCents
    }

    if (!discountType || !discountValue) return 0

    if (discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE) {
      return Math.min(Math.round(subtotalCents * (discountValue / 100)), subtotalCents)
    }
    return Math.min(dollarsToCents(discountValue), subtotalCents)
  }, [isReadOnly, existingTotals, discountType, discountValue, subtotalCents])

  // 3. Discount Ratio (for proportional tax calculation)
  const discountRatio = React.useMemo(() => {
    return subtotalCents > 0
      ? (subtotalCents - discountAmountCents) / subtotalCents
      : 1
  }, [subtotalCents, discountAmountCents])

  // 4. Tax (per-line-item with discount ratio applied)
  const taxAmountCents = React.useMemo(() => {
    if (isReadOnly && existingTotals) {
      return existingTotals.salesTaxCents
    }
    return items.reduce((sum, item) => {
      if (!item.isTaxable || !item.salesTaxRate) return sum
      const itemTotal = calculateLineTotalCents(item.quantity, item.unitPriceCents)
      const itemAfterDiscount = Math.round(itemTotal * discountRatio)
      const itemTax = Math.round(itemAfterDiscount * item.salesTaxRate)
      return sum + itemTax
    }, 0)
  }, [isReadOnly, existingTotals, items, discountRatio])

  // 5. Enforced Tip (calculated from percentage on subtotal after discount)
  const enforcedTipCents = React.useMemo(() => {
    if (isReadOnly && existingTotals) {
      return existingTotals.enforcedTipCents || 0
    }

    if (enforcedTipPercentage && enforcedTipPercentage > 0) {
      const subtotalAfterDiscountCents = subtotalCents - discountAmountCents
      return Math.round(subtotalAfterDiscountCents * (enforcedTipPercentage / 100))
    }

    return 0
  }, [isReadOnly, existingTotals, enforcedTipPercentage, subtotalCents, discountAmountCents])

  // 6. Tip to use (enforced tip or manual tip, whichever is greater)
  // For quotes: only enforced tip
  // For invoices: max(enforced, manual)
  const tipCents = React.useMemo(() => {
    return Math.max(enforcedTipCents, manualTipCents)
  }, [enforcedTipCents, manualTipCents])

  // 7. Total (tip-exclusive)
  const totalCents = React.useMemo(() => {
    if (isReadOnly && existingTotals) {
      return existingTotals.totalCents
    }
    return subtotalCents + taxAmountCents - discountAmountCents
  }, [isReadOnly, existingTotals, subtotalCents, taxAmountCents, discountAmountCents])

  // 8. Grand Total (tip-inclusive)
  const grandTotalCents = React.useMemo(() => {
    return totalCents + tipCents
  }, [totalCents, tipCents])

  return {
    subtotalCents,
    discountAmountCents,
    discountRatio,
    taxAmountCents,
    enforcedTipCents,
    tipCents,
    totalCents,
    grandTotalCents,
  }
}
